import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';

export interface TCUHealthData {
  vin: string;
  imei: string;
  networkOperator: string;
  boxCoverStatus: boolean | null;
  address: string;
  tcuBatlevel: string;
  firmwareVersion: string;
  serverTS: string;
  latitude: string;
  longitude: string;
  gsmSignal: string;
  ticketId: string;
  ignitionTS: string;
  vehBatVol: string;
  fuelTemp: string;
  adblueLevel: string;
  odoMeter: string;
  hourMeter: string;
  dtc: string;
  warningLight: string;
  cleanerChokeIndicator: string;
  lowOilPressure: string;
  oilPassFilter: string;
  dpfLampIndicator: string;
  dpfActiveRegeneration: string;
  dpfInhibitRegeneration: string;
  massPercentage: string;
  waterInFuelLevel: string;
  torqueReduction: string;
  speedReduction: string;
  ecoMode: string;
  engineRmp: string;
  coolantTemp: string;
  oilTemp: string;
  gearIndicator: string;
  createdByName: string;
  createdById: string;
  fileName: string;
  helpButtonStatus: boolean | null;
  packetType: string;
}

interface GenerateEoltReportDialogProps {
  open: boolean;
  onClose: () => void;
  rowData: any | null;
  tcuHealthData: TCUHealthData | null;
  tcuHealthLoading: boolean;
  onConfirmGenerate: (vin: string) => void;
  generating: boolean;
}

// boxCoverStatus / helpButtonStatus are booleans. `{value || 'N/A'}` showed
// "N/A" for false and nothing at all for true (React renders no text for a
// boolean), so the one thing the operator was checking was never visible.
// Labels as the legacy tool and the PDF print them.
const onOff = (value: boolean | null | undefined, on: string, off: string): string =>
  value === null || value === undefined ? 'N/A' : value ? on : off;

const formatTimestamp = (ts: string | null | undefined): string => {
  if (!ts) return 'N/A';
  const num = Number(ts);
  if (!isNaN(num) && num > 0) {
    const date = new Date(num * 1000);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  }
  return ts;
};

const GenerateEoltReportDialog: React.FC<GenerateEoltReportDialogProps> = ({
  open,
  onClose,
  rowData,
  tcuHealthData,
  tcuHealthLoading,
  onConfirmGenerate,
  generating,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsConfirmed(false);
    }
  }, [open]);

  const handleSubmit = () => {
    if (rowData?.vin && isConfirmed) {
      onConfirmGenerate(rowData.vin);
    }
  };

  if (!rowData) return null;

  // The list row carries fileName once a report exists; regenerating replaces it.
  const replacesReport = !!rowData.fileName || !!rowData.hasReport;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem',ml:-1}}>
        TCU Health Parameters
      </DialogTitle>

      <DialogContent>
        {replacesReport && (
          <Typography variant="body2" sx={{ mb: 2, color: '#B26A00' }}>
            A report already exists for this vehicle. Submitting replaces it with a new one built from the latest data.
          </Typography>
        )}
        {tcuHealthLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={36} />
            <Typography sx={{ ml: 2 }}>Fetching TCU health data...</Typography>
          </Box>
        ) : tcuHealthData ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              {/* Left Column */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  TCU IMEI mapping
                </Typography>
                <Typography variant="body2" sx={{ color: '#808080' }}>{tcuHealthData.imei || 'N/A'}</Typography>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Box cover status
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}>{onOff(tcuHealthData.boxCoverStatus, 'Open', 'Closed')}</Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Location
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}>{tcuHealthData.address || 'N/A'}</Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    TCU Firmware version
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}> {tcuHealthData.firmwareVersion || 'N/A'}</Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Emergency Button Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}> {onOff(tcuHealthData.helpButtonStatus, 'ON', 'OFF')}</Typography>
                </Box>
              </Box>

              {/* Right Column */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Network operator
                </Typography>
                <Typography variant="body2" sx={{ color: '#808080' }}>{tcuHealthData.networkOperator || 'N/A'}</Typography>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    GSM Signal
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}> {tcuHealthData.gsmSignal || 'N/A'}</Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    TCU battery level
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}> {tcuHealthData.tcuBatlevel ? `${tcuHealthData.tcuBatlevel}%` : 'N/A'}</Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Recent DATA timestamp
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#808080' }}> {formatTimestamp(tcuHealthData.serverTS)}</Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I hereby confirm that the EOLT information provided in this statement is true and accurate to the best of my knowledge.
                </Typography>
              }
            />
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography color="error">Failed to load TCU health data.</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button
          variant="contained"
          onClick={onClose}
          disabled={generating}
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': { backgroundColor: '#333' },
            borderRadius: '20px',
            px: 4,
          }}
        >
          Reject
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isConfirmed || generating || tcuHealthLoading}
          sx={{
            backgroundColor: '#E60012',
            color: '#fff',
            '&:hover': { backgroundColor: '#ff0015ff' },
            borderRadius: '20px',
            px: 4,
          }}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {generating ? 'Generating...' : replacesReport ? 'Replace report' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateEoltReportDialog;